import React, { useState } from 'react';
import styled from 'styled-components';

import { Alert, Stack, TextField as MuiTextField } from '@mui/material';
import { FieldError } from '../../../Assistant';
import { Label } from '@common/Label';
import { AIToolMetadata, BucketRetrievalMetadata, ToolDrawerOpen, ToolExecution, ToolType } from '../../tool.types';
import { BucketRetrievalExecution } from '../AssistantToolExecution/BucketRetrieval/BucketRetrievalExecution';
import { SubText } from '@common/SubText/SubText';
import { Expression } from '@common/Expression';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

// Custom TextField component to avoid ResizeObserver issues
const TextField = styled(MuiTextField)`
  & .MuiInputBase-root {
    height: auto;
  }

  & .MuiInputBase-inputMultiline {
    overflow-y: auto !important;
  }
`;

interface Props {
  openTool: ToolDrawerOpen;
  toolConfig: {
    toolName: string;
    setToolName: React.Dispatch<React.SetStateAction<string>>;
    toolDescription: string;
    setToolDescription: React.Dispatch<React.SetStateAction<string>>;
    toolInstructions: string;
    setToolInstructions: React.Dispatch<React.SetStateAction<string>>;
    toolMetadata: AIToolMetadata;
    setToolMetadata: React.Dispatch<React.SetStateAction<AIToolMetadata>>;
  };
}

const convertToolName = (input: string) => {
  let formatted = input.replace(/ /g, '_');
  formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
  return formatted.toLowerCase();
};

export const AssistantToolConfig: React.FC<Props> = ({ openTool, toolConfig }) => {
  const { tool } = openTool;
  const { toolName, setToolName, toolDescription, setToolDescription, toolInstructions, setToolInstructions } =
    toolConfig;

  const [fieldError, setFieldError] = useState<FieldError>(null);

  const isHangupVoiceTool = tool.type === ToolType.VOICE && tool.execution === ToolExecution.HANGUP_CALL;

  const updateField = (value: string, updateMethod: React.Dispatch<React.SetStateAction<string>>) => {
    updateMethod(value);
    setFieldError(null);
  };

  return (
    <Container>
      {tool.execution !== ToolExecution.BUCKET_RETRIEVAL && (
        <Stack spacing={1}>
          <Label title='Name' tooltip='The name of the tool.' />
          <TextField
            fullWidth
            size='small'
            disabled={isHangupVoiceTool || tool.execution === ToolExecution.UNSUPPORTED}
            value={toolName}
            error={fieldError?.id === 'toolName'}
            helperText={fieldError?.id === 'toolName' ? fieldError.error : undefined}
            onChange={(e) => updateField(convertToolName(e.target.value), setToolName)}
            placeholder='Tool Name'
          />
          {isHangupVoiceTool && <SubText>The name cannot be changed for the hangup tool.</SubText>}
        </Stack>
      )}
      {tool.execution !== ToolExecution.BUCKET_RETRIEVAL && (
        <Stack spacing={1}>
          <Label
            title='Description'
            tooltip={
              tool.type === ToolType.VOICE
                ? 'A description of when this tool should be used. It is important to be specific as the LLM will read this description to determine when to use this tool.'
                : 'A description of when this tool should be used. It is important to be specific as the LLM will read this description to determine when to use this tool. The LLM will also read the arguments available (Arguments Tab) when factoring whether to run this tool.'
            }
          />
          {tool.execution !== ToolExecution.UNSUPPORTED && (
            <Expression
              key={`description`}
              keyProp={`description`}
              value={toolDescription}
              error={fieldError?.id === 'toolDescription' ? fieldError.error : undefined}
              title={'Tool Description'}
              description={
                'A description of when this tool should be used. It is important to be specific as the LLM will read this description to determine when to use this tool.'
              }
              setValue={(value: string) => updateField(value, setToolDescription)}
              options={{}}
              minRows={1}
              maxRows={12}
              placeholder='Tool Description'
            />
          )}
        </Stack>
      )}
      {tool.type === ToolType.FLOW && (
        <Stack spacing={1}>
          <Label
            title='Instructions'
            tooltip='Optionally defined to overwrite the instructions of the assistant after this tool is executed. If left blank, the assistant will use the last used instructions.'
          />
          <Expression
            key={`instructions`}
            keyProp={`instructions`}
            value={toolInstructions}
            error={fieldError?.id === 'toolInstructions' ? fieldError.error : undefined}
            title={'Tool Instructions'}
            description={
              'Optionally defined to overwrite the instructions of the assistant after this tool is executed. If left blank, the assistant will use the last used instructions.'
            }
            setValue={(value: string) => updateField(value, setToolInstructions)}
            options={{}}
            minRows={4}
            maxRows={12}
            placeholder='Tool Instructions'
          />
        </Stack>
      )}
      {tool.execution === ToolExecution.BUCKET_RETRIEVAL && (
        <BucketRetrievalExecution
          metadata={toolConfig.toolMetadata as BucketRetrievalMetadata}
          setToolMetadata={toolConfig.setToolMetadata as React.Dispatch<React.SetStateAction<BucketRetrievalMetadata>>}
        />
      )}
      {tool.execution === ToolExecution.UNSUPPORTED && (
        <Alert severity='warning'>User defined functions are currently not supported for voice integrations.</Alert>
      )}
    </Container>
  );
};
